/*
 * /weather — two-model consensus forecast.
 *
 * The page's one non-obvious job: never present a single model's number as
 * fact. Everything visual here exists to carry the honest version of the
 * answer — the spread band on the chart, the "N of M models" rain verdict, the
 * distance to the nearest real thermometer, the model run time.
 *
 * Reuses, rather than reimplements:
 *   window.LocationInput  city search / geolocation / reverse geocode (9 locales)
 *   window.GlobeAstro     sunrise / sunset / moon phase (incl. polar day & night)
 *   window.__toolSettings units preference + per-tool persistence
 *   window.__toast / __handleError / __announce
 */
(function () {
'use strict';

var CFG = window.WX_CFG || {};
var T   = CFG.t || {};
var DIR = CFG.dir || 'ltr';

function t(k, vars) {
    var s = T[k] != null ? T[k] : k;
    if (!vars) return s;
    /* Longest placeholder FIRST. Replacing in object order lets a short key eat
       a longer one that starts with it: with {n, norm} the ':n' pass turned
       ':norm' into '31orm' on the page. Same class of bug as ':n' swallowing
       ':name' elsewhere in this codebase — it only ever shows up on a
       screenshot, never in a test that checks the key exists. */
    var keys = Object.keys(vars).sort(function (a, b) { return b.length - a.length; });
    for (var i = 0; i < keys.length; i++) {
        s = s.split(':' + keys[i]).join(vars[keys[i]]);
    }
    return s;
}

var $ = function (sel, root) { return (root || document).querySelector(sel); };

/* ── state ─────────────────────────────────────────────────────────────── */
var state = {
    loc:   null,   // { lat, lng, name, tz, cc }
    data:  null,   // API payload
    days:  [],     // grouped
    sel:   0,      // selected day index
    units: 'metric',
    layer: 'temp',   // temp | rain | wind
    hour:  null,     // index of the hour opened in the detail panel
    loading: false
};

/* ── units ─────────────────────────────────────────────────────────────── */
function isImperial() { return state.units === 'imperial'; }

function temp(c)  { return c == null ? null : (isImperial() ? c * 9 / 5 + 32 : c); }
function tempS(c, dp) {
    var v = temp(c);
    if (v == null) return '—';
    return (dp ? v.toFixed(dp) : Math.round(v)) + '°';
}
/* MET ships m/s. Imperial → mph; metric → km/h, which is what almost everyone
   outside meteorology actually reads wind in. */
function wind(ms) { return ms == null ? null : (isImperial() ? ms * 2.236936 : ms * 3.6); }
function windS(ms) {
    var v = wind(ms);
    return v == null ? '—' : Math.round(v) + ' ' + (isImperial() ? t('u_mph') : t('u_kmh'));
}
function rainS(mm) {
    if (mm == null) return '—';
    if (isImperial()) {
        var i = mm / 25.4;
        return (i < 0.01 && i > 0 ? '<0.01' : i.toFixed(2)) + ' ' + t('u_in');
    }
    return (mm < 0.1 && mm > 0 ? '<0.1' : (mm < 1 ? mm.toFixed(1) : Math.round(mm * 10) / 10)) + ' ' + t('u_mm');
}
function distS(km) {
    if (km == null) return '';
    return isImperial() ? (km * 0.621371).toFixed(1) + ' ' + t('u_mi') : km + ' ' + t('u_km');
}

/* ── weather symbol → localized label + icon key ────────────────────────
   MET publishes ~50 symbol codes (intensity × type × showers × thunder ×
   day/night). Rather than translate 50 strings nine times, the code is
   decomposed: a base label plus an optional "with thunder" suffix. */
function symParts(sym) {
    if (!sym) return null;
    var s = String(sym);
    var night = /_night$/.test(s);
    var polar = /_polartwilight$/.test(s);
    s = s.replace(/_(day|night|polartwilight)$/, '');
    var thunder = /andthunder$/.test(s);
    s = s.replace(/andthunder$/, '');
    s = s.replace(/showers$/, '');           // showers read the same to a user
    return { base: s, thunder: thunder, night: night, polar: polar };
}

function symLabel(sym) {
    var p = symParts(sym);
    if (!p) return '';
    var label = t('sym_' + p.base);
    if (label === 'sym_' + p.base) label = t('sym_cloudy');
    return p.thunder ? t('sym_with_thunder', { c: label }) : label;
}

/* Glyph choice lives in public/js/weather-icons.js — shared with the profile
   widget so markup and stylesheet can never ship apart again. */
function symIcon(sym) { return window.WeatherIcons.iconFor(sym); }

function icon(key, cls) { return window.WeatherIcons.svg(key, cls); }

/* ── time helpers, always in the LOCATION's timezone ─────────────────────
   A forecast rendered in the viewer's timezone is wrong the moment they look
   up another city — "tomorrow" silently becomes tomorrow-in-Adelaide while
   the user is reading about Moscow. Every bucket below is computed with the
   location's IANA zone, which LocationInput already carries per city. */
function tz() { return (state.loc && state.loc.tz) || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; }

function fmt(d, opts) {
    try { return new Intl.DateTimeFormat(CFG.locale || 'en', Object.assign({ timeZone: tz() }, opts)).format(d); }
    catch (_) { return new Intl.DateTimeFormat(CFG.locale || 'en', opts).format(d); }
}
/* Stable YYYY-MM-DD in the location's zone — en-CA gives ISO order. */
function dayKey(d) {
    try { return new Intl.DateTimeFormat('en-CA', { timeZone: tz(), year: 'numeric', month: '2-digit', day: '2-digit' }).format(d); }
    catch (_) { return d.toISOString().slice(0, 10); }
}
function hourS(d) { return fmt(d, { hour: 'numeric', minute: '2-digit', hour12: undefined }); }

/* Minutes-after-local-midnight → HH:MM in the location's zone. GlobeAstro
   returns LOCAL APPARENT SOLAR minutes, which must be corrected for the
   longitude offset from the zone's meridian before it means clock time. */
function solarToClock(minutes, date) {
    if (minutes == null) return null;
    var lonCorr = 4 * state.loc.lng;                 // minutes of solar time east of Greenwich
    var utcMin  = minutes - lonCorr;                 // → minutes UTC
    var base    = Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
    return new Date(base + utcMin * 60000);
}

/* ── confidence, derived from the two models' spread ─────────────────────
   Calibrated against a real 8-city / 634-hour sample (2026-08-09):
   median |ΔT| 0.90 °C, p90 2.80 °C. So "high" is the ordinary case and "low"
   is genuinely worse than nine hours in ten — the band does not cry wolf. */
function confOf(spread) {
    if (spread == null) return null;
    if (spread <= 1.5) return 'high';
    if (spread <= 3.0) return 'med';
    return 'low';
}

/* ── grouping ───────────────────────────────────────────────────────────── */
function group(data) {
    var byDay = {}, order = [];
    data.hours.forEach(function (h) {
        var d = new Date(h.t);
        if (isNaN(d)) return;
        h._d = d;
        var k = dayKey(d);
        if (!byDay[k]) { byDay[k] = { key: k, date: d, hours: [] }; order.push(k); }
        byDay[k].hours.push(h);
    });
    /* MET's series starts at the current hour and ends mid-day nine days out,
       so grouping yields a partial first day (correct — that IS the rest of
       today) and a trailing FRAGMENT of one or two blocks. Rendering the
       fragment as a day gives it a nonsense high/low drawn from a couple of
       night-time hours. Today always stays; a tail day needs real coverage. */
    var days = order.map(function (k, i) {
        var d = byDay[k];
        d._frag = i > 0 && d.hours.length < 2;
        return d;
    });
    while (days.length && days[days.length - 1]._frag) days.pop();
    return days.slice(0, 9).map(function (day) {
        var hs  = day.hours;
        var temps = hs.map(function (h) { return h.temp; }).filter(function (v) { return v != null; });
        day.hi = temps.length ? Math.max.apply(null, temps) : null;
        day.lo = temps.length ? Math.min.apply(null, temps) : null;

        /* Rain total must respect `span`: a 6-hour block's amount covers six
           hours, so summing blocks as if each were an hour would silently
           multiply late-range precipitation. */
        var mm = 0, hasRain = false;
        hs.forEach(function (h) { if (h.rain != null) { mm += h.rain; hasRain = true; } });
        day.mm = hasRain ? Math.round(mm * 10) / 10 : null;

        var sp = hs.map(function (h) { return h.spread; }).filter(function (v) { return v != null; });
        day.spread = sp.length ? Math.round((sp.reduce(function (a, b) { return a + b; }, 0) / sp.length) * 10) / 10 : null;
        day.conf = confOf(day.spread);

        /* Representative symbol: the daytime block with the "worst" weather,
           because a day with one thunderstorm is a thunderstorm day. */
        var rank = { 'thunder': 9, 'rain-heavy': 8, 'snow': 7, 'sleet': 6, 'rain': 5, 'fog': 4, 'cloud': 3, 'sun-cloud': 2, 'moon-cloud': 2, 'sun': 1, 'moon': 0 };
        var best = null, bestR = -1;
        hs.forEach(function (h) {
            if (!h.sym) return;
            var r = rank[symIcon(h.sym)] || 0;
            if (r > bestR) { bestR = r; best = h.sym; }
        });
        day.sym = best;

        /* Model split: hours where the two models disagreed about rain at all. */
        day.split = hs.filter(function (h) { return h.nvote === 2 && h.votes === 1; }).length;
        day.wet   = hs.filter(function (h) { return h.nvote === 2 && h.votes === 2; }).length;
        var pops = hs.map(function (h) { return h.pop; }).filter(function (v) { return v != null; });
        day.pop = pops.length ? Math.max.apply(null, pops) : null;
        return day;
    });
}

/* ── "has this forecast changed since the last model run?" ────────────────
   Grouped in the LOCATION's timezone, the same buckets the day cards use, so
   the sentence and the card can never disagree. Returns null unless the change
   is big enough to be worth a user's attention: forecasts wobble by tenths of a
   degree constantly and reporting that would train people to ignore the block. */
function changeFor(day) {
    var prev = state.data.prev;
    if (!prev || !prev.hours || !prev.hours.length) return null;

    var his = [], los = [], mm = 0, seen = 0;
    for (var i = 0; i < prev.hours.length; i++) {
        var row = prev.hours[i];
        var d = new Date(row[0]);
        if (isNaN(d) || dayKey(d) !== day.key) continue;
        seen++;
        if (row[1] != null) { his.push(row[1]); los.push(row[1]); }
        if (row[2] != null) mm += row[2];
    }
    /* A day the old run only partly covered (its series started later) would
       produce a fake "high" from a couple of night hours. */
    if (seen < Math.max(3, day.hours.length * 0.6) || !his.length) return null;

    var oHi = Math.max.apply(null, his), oLo = Math.min.apply(null, los);
    var dHi = day.hi - oHi;
    var rainNow = day.mm || 0, rainWas = Math.round(mm * 10) / 10;

    var tempMoved = Math.abs(dHi) >= 2;
    var rainAppeared = rainWas < 0.2 && rainNow >= 0.5;
    var rainGone     = rainWas >= 0.5 && rainNow < 0.2;
    if (!tempMoved && !rainAppeared && !rainGone) return null;

    return { dHi: dHi, oHi: oHi, rainAppeared: rainAppeared, rainGone: rainGone, issued: prev.issued };
}

function renderChange() {
    var box = $('#wx-change-note');
    if (!box) return;
    var day = state.days[state.sel];
    var c = day ? changeFor(day) : null;
    if (!c) { box.hidden = true; box.innerHTML = ''; return; }

    var parts = [];
    if (Math.abs(c.dHi) >= 2) {
        parts.push(t(c.dHi > 0 ? 'chg_warmer' : 'chg_colder', {
            d: Math.abs(Math.round(c.dHi)), was: tempS(c.oHi), now: tempS(day.hi)
        }));
    }
    if (c.rainAppeared) parts.push(t('chg_rain_appeared'));
    if (c.rainGone)     parts.push(t('chg_rain_gone'));

    box.hidden = false;
    box.innerHTML = '<strong>' + esc(t('chg_title')) + '</strong> ' + esc(parts.join(' ')) +
        ' <span class="wx-chg-when">' + esc(t('chg_since', {
            ago: agoS(c.issued)
        })) + '</span>';
}

function agoS(iso) {
    var m = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
    return m < 60 ? t('min_ago', { n: m }) : t('h_ago', { n: Math.round(m / 60) });
}

/* Current hour = the last block whose start is <= now (blocks are forward-
   looking). Falls back to the first block for a just-issued run. */
function nowHour() {
    var now = Date.now(), cur = state.data.hours[0];
    for (var i = 0; i < state.data.hours.length; i++) {
        var h = state.data.hours[i];
        if (h._d && h._d.getTime() <= now) cur = h; else break;
    }
    return cur;
}

/* ── rendering ──────────────────────────────────────────────────────────── */
function esc(s) { return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) { return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]; }); }

function renderAll() {
    if (!state.data || !state.data.hours || !state.data.hours.length) return;
    state.days = group(state.data);
    if (state.sel >= state.days.length) state.sel = 0;
    renderHero();
    renderDays();
    renderHourly();
    renderBest();
    renderChange();
    renderDetails();
    renderProvenance();
    $('#wx-body').hidden = false;
}

function renderHero() {
    var h = nowHour();
    var today = state.days[0];
    var conf = confOf(h.spread);
    var el = $('#wx-hero');

    var confHtml = '';
    if (conf) {
        confHtml =
            '<button type="button" class="wx-conf c-' + conf + '" data-explain="conf" ' +
                'aria-label="' + esc(t('conf_' + conf) + '. ' + t('conf_explain_short')) + '">' +
                '<span class="wx-conf-dot"></span>' + esc(t('conf_' + conf)) +
                '<span class="wx-conf-q" aria-hidden="true">?</span>' +
            '</button>';
    }

    /* Today's high/low belongs in the hero, not only in the day strip: "how
       cold does it get tonight" is a first-screen question on every major
       weather app and ours made you hunt for it. */
    var hilo = today && today.hi != null
        ? '<span class="wx-hilo"><b>' + tempS(today.hi) + '</b><i>' + tempS(today.lo) + '</i></span>'
        : '';

    el.innerHTML =
        '<div class="wx-hero-main">' +
            '<div class="wx-hero-ic">' + icon(symIcon(h.sym), 'big') + '</div>' +
            '<div class="wx-hero-t">' +
                '<div class="wx-temp">' + tempS(h.temp) + hilo + '</div>' +
                '<div class="wx-cond">' + esc(symLabel(h.sym)) + '</div>' +
                '<div class="wx-feels">' + esc(t('feels', { v: tempS(h.feels) })) +
                    (h.tlo != null && h.spread >= 0.5
                        ? ' <span class="wx-band" title="' + esc(t('band_title')) + '">' + tempS(h.tlo) + '–' + tempS(h.thi) + '</span>'
                        : '') +
                '</div>' +
            '</div>' +
        '</div>' +
        '<div class="wx-hero-side">' + confHtml + renderVerdict() + '</div>';
}

/* The headline honesty statement. Three genuinely different answers, none of
   them a fabricated percentage. */
function renderVerdict() {
    var next = [], now = Date.now();
    for (var i = 0; i < state.data.hours.length && next.length < 12; i++) {
        var h = state.data.hours[i];
        if (h._d && h._d.getTime() >= now - 3600e3) next.push(h);
    }
    var voted = next.filter(function (h) { return h.nvote > 0; });
    if (!voted.length) return '';

    var both  = voted.filter(function (h) { return h.nvote === 2 && h.votes === 2; });
    var split = voted.filter(function (h) { return h.nvote === 2 && h.votes === 1; });
    var one   = voted.filter(function (h) { return h.nvote === 1 && h.votes === 1; });

    var cls, txt, sub = '';
    if (both.length) {
        cls = 'v-wet';
        txt = t('verdict_rain', { n: both.length });
        sub = t('verdict_rain_from', { time: hourS(both[0]._d) });
    } else if (split.length) {
        cls = 'v-split';
        txt = t('verdict_split', { n: split.length });
        sub = t('verdict_split_sub');
    } else if (one.length) {
        cls = 'v-split';
        txt = t('verdict_single_model');
        sub = t('verdict_single_model_sub');
    } else {
        cls = 'v-dry';
        txt = t('verdict_dry');
        sub = state.data.models.length > 1 ? t('verdict_dry_sub') : '';
    }
    return '<div class="wx-verdict ' + cls + '"><strong>' + esc(txt) + '</strong>' +
           (sub ? '<span>' + esc(sub) + '</span>' : '') + '</div>';
}

/* A day's solar anchor.
   `day.date` is that day's FIRST hour, and in any positive-offset zone that
   instant belongs to the PREVIOUS UTC date — Moscow's 10 Aug starts at
   09 Aug 21:00Z. solarToClock() reads UTC date components, so passing day.date
   computed sunrise for the wrong calendar day; the times looked right (sunrise
   moves ~2 min/day) but every hour then fell outside the window and
   "best time to go out" silently never appeared. Anchoring at 12:00Z of the
   day's own key makes getUTCDate() the day we mean, for every zone. */
function dayAnchor(day) {
    var d = new Date(day.key + 'T12:00:00Z');
    return isNaN(d) ? day.date : d;
}

/* ── "best window today" ──────────────────────────────────────────────────
   Every weather site hands you numbers and leaves the decision to you. This
   answers the question people actually came with — "when should I go out?" —
   from data already on the page, with the rule stated so it can be argued with:
   dry hours, in daylight, ranked by how close the apparent temperature is to
   comfortable and how little wind there is. Shown only when the day HAS both a
   good stretch and a worse one; a uniformly fine day needs no advice, and a
   uniformly wet one would only get a least-bad answer dressed as a good one. */
function bestWindow(day) {
    if (!day) return null;
    var hs = day.hours.filter(function (h) { return h.span === 1 && h.temp != null; });
    if (hs.length < 6) return null;

    var now = Date.now();
    var rise = null, set = null;
    if (window.GlobeAstro && state.loc) {
        var anchor = dayAnchor(day);
        var rs = window.GlobeAstro.sunRiseSetLocal(state.loc.lat, anchor);
        if (rs.state === 'normal') {
            rise = solarToClock(rs.rise, anchor);
            set  = solarToClock(rs.set, anchor);
        }
    }

    function score(h) {
        if (h.rain != null && h.rain >= 0.1) return null;          // wet hours are out
        if (h._d.getTime() < now - 1800e3) return null;            // already past
        if (rise && set && (h._d < rise || h._d > set)) return null; // after dark
        var feels = h.feels != null ? h.feels : h.temp;
        var s = 100;
        s -= Math.abs(feels - 21) * 4;                             // 21 °C ≈ comfortable
        s -= Math.max(0, (h.wind || 0) - 3) * 5;                   // a breeze above ~3 m/s
        s -= Math.max(0, (h.cloud || 0) - 60) / 10;                // heavy overcast, mildly
        return s;
    }

    /* Pick the best SHORT stretch, not the longest dry one. The first cut
       returned "05:00–21:00, 16 h" for an ordinary Moscow day — technically the
       longest run that satisfied every rule, and useless as advice: "go out
       today" is not a recommendation. People want a couple of hours they can
       put in a calendar, so we score every contiguous 2–5 hour window and take
       the best-rated one. */
    var scored = hs.map(score);
    var okAll  = scored.filter(function (v) { return v !== null; });
    if (okAll.length < 3) return null;
    var dayMean = okAll.reduce(function (a, b) { return a + b; }, 0) / okAll.length;

    var best = null;
    for (var len = 5; len >= 2; len--) {
        for (var i = 0; i + len <= hs.length; i++) {
            var sum = 0, ok = true;
            for (var j = i; j < i + len; j++) {
                if (scored[j] === null) { ok = false; break; }
                sum += scored[j];
            }
            if (!ok) continue;
            var mean = sum / len;
            /* Ties go to the LONGER window (the outer loop starts at 5), so a
               flat good afternoon reads as one block rather than an arbitrary
               two hours inside it. */
            if (!best || mean > best.mean + 0.01) best = { i: i, len: len, mean: mean };
        }
    }
    if (!best) return null;

    /* No contrast → no advice. A uniformly pleasant day does not need to be
       told apart, and a uniformly bad one would get a least-bad answer dressed
       up as a good one. */
    if (best.mean - dayMean < 6) return null;

    return { from: hs[best.i], to: hs[best.i + best.len - 1], hours: best.len };

}

function renderBest() {
    var box = $('#wx-best');
    if (!box) return;
    var w = bestWindow(state.days[state.sel]);
    if (!w) { box.hidden = true; box.innerHTML = ''; return; }
    var end = new Date(w.to._d.getTime() + 3600e3);
    box.hidden = false;
    box.innerHTML = '<span class="wx-best-ic" aria-hidden="true">☀</span>' +
        '<span><strong>' + esc(t('best_title')) + '</strong> ' +
        esc(t('best_body', { from: hourS(w.from._d), to: hourS(end), n: w.hours })) + '</span>';
}

function renderDays() {
    var el = $('#wx-days');
    var all = state.days.map(function (d) { return [d.lo, d.hi]; }).reduce(function (a, b) { return a.concat(b); }, [])
              .filter(function (v) { return v != null; });
    var min = Math.min.apply(null, all), max = Math.max.apply(null, all);
    var range = Math.max(1, max - min);

    el.innerHTML = state.days.map(function (d, i) {
        var l = ((d.lo - min) / range) * 100, w = Math.max(6, ((d.hi - d.lo) / range) * 100);
        var isToday = d.key === dayKey(new Date());
        return '<button type="button" class="wx-day' + (i === state.sel ? ' on' : '') + '" data-day="' + i + '" ' +
               'aria-pressed="' + (i === state.sel) + '">' +
            '<span class="wx-day-n">' + esc(isToday ? t('today') : fmt(d.date, { weekday: 'short' })) + '</span>' +
            '<span class="wx-day-d">' + esc(fmt(d.date, { day: 'numeric', month: 'short' })) + '</span>' +
            '<span class="wx-day-i">' + icon(symIcon(d.sym)) + '</span>' +
            '<span class="wx-day-bar"><span class="wx-day-fill" style="inset-inline-start:' + l.toFixed(1) + '%;width:' + w.toFixed(1) + '%"></span></span>' +
            '<span class="wx-day-t"><b>' + tempS(d.hi) + '</b><i>' + tempS(d.lo) + '</i></span>' +
            (d.mm ? '<span class="wx-day-mm">' + esc(rainS(d.mm)) + '</span>' : '<span class="wx-day-mm"></span>') +
            (d.split ? '<span class="wx-day-split" title="' + esc(t('split_hours', { n: d.split })) + '">◑</span>' : '') +
        '</button>';
    }).join('');

    var legend = $('#wx-days-legend');
    var anySplit = state.days.some(function (d) { return d.split > 0; });
    legend.hidden = !anySplit;
    if (anySplit) legend.innerHTML = '<span class="wx-day-split" aria-hidden="true">◑</span> ' + esc(t('legend_split'));
}

/* ── hourly chart, three switchable layers ────────────────────────────────
   Temperature / precipitation / wind on the same timeline, one at a time.
   Sharing an axis with two of them stacked (the previous design) meant the
   rain bars were a footnote and wind was not shown at all, though we hold it
   for every hour. Switching layers keeps each readable at full height.
   SVG, not canvas: crisp at any DPR and the numbers stay in the DOM. */
function renderHourly() {
    var day = state.days[state.sel];
    if (!day) return;
    var hs = day.hours.filter(function (h) { return h.temp != null; });
    if (!hs.length) return;

    var coarse = hs.some(function (h) { return h.span === 6; });
    var layer = state.layer;

    var W = Math.max(560, hs.length * 64), H = 224, padT = 52, padB = 52;
    var step = W / hs.length;
    var rtl = DIR === 'rtl';
    var x = function (i) { return rtl ? W - (i + 0.5) * step : (i + 0.5) * step; };

    var body = '';
    if (layer === 'rain')      body = layerRain(hs, x, step, W, H, padT, padB);
    else if (layer === 'wind') body = layerWind(hs, x, step, W, H, padT, padB);
    else                       body = layerTemp(hs, x, W, H, padT, padB);

    var labels = hs.map(function (h, i) {
        var every = hs.length > 14 ? 3 : (hs.length > 8 ? 2 : 1);
        if (i % every !== 0) return '';
        return '<text class="wx-xl" x="' + x(i).toFixed(1) + '" y="' + (H - 8) + '">' + esc(hourS(h._d)) + '</text>';
    }).join('');

    var icons = hs.map(function (h, i) {
        var every = hs.length > 14 ? 3 : (hs.length > 8 ? 2 : 1);
        if (i % every !== 0 || !h.sym) return '';
        return '<g transform="translate(' + (x(i) - 9).toFixed(1) + ',4)">' +
               '<svg class="wx-ic chart i-' + symIcon(h.sym) + '" width="18" height="18" viewBox="0 0 24 24">' +
               (window.WeatherIcons.paths[symIcon(h.sym)] || window.WeatherIcons.paths.cloud) + '</svg></g>';
    }).join('');

    /* One transparent hit-target per hour: tapping shows that hour's full
       reading, which is otherwise only available for "now". */
    var hits = hs.map(function (h, i) {
        return '<rect class="wx-hit" data-hour="' + i + '" x="' + (x(i) - step / 2).toFixed(1) + '" y="0" ' +
               'width="' + step.toFixed(1) + '" height="' + H + '"><title>' + esc(hourS(h._d)) + '</title></rect>';
    }).join('');

    $('#wx-chart').innerHTML =
        (coarse ? '<p class="wx-coarse">' + esc(t('coarse_note')) + '</p>' : '') +
        '<div class="wx-chart-scroll"><svg viewBox="0 0 ' + W + ' ' + H + '" width="' + W + '" height="' + H + '" role="img" ' +
            'aria-label="' + esc(t('chart_sr', { day: fmt(day.date, { weekday: 'long', day: 'numeric', month: 'long' }) })) + '">' +
            body + icons + labels + hits +
        '</svg></div>';

    $('#wx-chart-table').innerHTML = hs.map(function (h) {
        return '<tr><th scope="row">' + esc(hourS(h._d)) + '</th><td>' + esc(tempS(h.temp)) + '</td>' +
               '<td>' + esc(symLabel(h.sym)) + '</td><td>' + esc(h.rain ? rainS(h.rain) : '—') + '</td>' +
               '<td>' + esc(h.wind != null ? windS(h.wind) : '—') + '</td></tr>';
    }).join('');

    renderHourDetail(null);
}

function rtlNow() { return DIR === 'rtl'; }

function layerTemp(hs, x, W, H, padT, padB) {
    var temps = hs.map(function (h) { return h.temp; });
    var los = hs.map(function (h) { return h.tlo != null ? h.tlo : h.temp; });
    var his = hs.map(function (h) { return h.thi != null ? h.thi : h.temp; });
    var min = Math.min.apply(null, los), max = Math.max.apply(null, his);
    if (max - min < 3) { var mid = (max + min) / 2; min = mid - 1.5; max = mid + 1.5; }
    var y = function (v) { return padT + (1 - (v - min) / (max - min)) * (H - padT - padB); };

    var band = his.map(function (v, i) { return x(i) + ',' + y(v); })
        .concat(los.map(function (v, i) { return x(i) + ',' + y(v); }).reverse()).join(' ');
    var line = temps.map(function (v, i) { return (i ? 'L' : 'M') + x(i) + ' ' + y(v); }).join(' ');
    /* The label sits above its point, but never inside the icon strip: with a
       flat day the curve hugs the top and the numbers landed on the glyphs. */
    var vals = hs.map(function (h, i) {
        var every = hs.length > 14 ? 2 : 1;
        if (i % every) return '';
        var ly = Math.max(padT - 6, y(h.temp) - 12);
        return '<text class="wx-tv" x="' + x(i).toFixed(1) + '" y="' + ly.toFixed(1) + '">' + esc(tempS(h.temp)) + '</text>';
    }).join('');

    return (his.some(function (v, i) { return v !== los[i]; })
              ? '<polygon class="wx-spread" points="' + band + '"></polygon>' : '') +
           '<path class="wx-line" d="' + line + '"/>' + vals;
}

function layerRain(hs, x, step, W, H, padT, padB) {
    var maxMm = Math.max.apply(null, hs.map(function (h) { return h.rain || 0; }).concat([1]));
    var base = H - padB;
    var bars = hs.map(function (h, i) {
        if (!h.rain) return '';
        var bh = Math.max(3, (h.rain / maxMm) * (base - padT));
        var w = Math.min(30, step * 0.55);
        var split = h.nvote === 2 && h.votes === 1;
        return '<rect class="wx-bar' + (split ? ' split' : '') + '" x="' + (x(i) - w / 2).toFixed(1) + '" y="' + (base - bh).toFixed(1) +
               '" width="' + w.toFixed(1) + '" height="' + bh.toFixed(1) + '" rx="3"></rect>' +
               '<text class="wx-tv sm" x="' + x(i).toFixed(1) + '" y="' + (base - bh - 6).toFixed(1) + '">' + esc(rainS(h.rain)) + '</text>';
    }).join('');
    var dry = hs.every(function (h) { return !h.rain; });
    var unit = dry ? '' : '<text class="wx-unit" x="' + (rtlNow() ? W - 6 : 6) + '" y="' + (padT + 4) + '" text-anchor="' +
               (rtlNow() ? 'end' : 'start') + '">' + esc(isImperial() ? t('u_in') : t('u_mm')) + '</text>';
    return '<line class="wx-axis" x1="0" y1="' + base + '" x2="' + W + '" y2="' + base + '"/>' + unit + bars +
        (dry ? '<text class="wx-empty" x="' + (W / 2) + '" y="' + ((padT + base) / 2) + '">' + esc(t('layer_no_rain')) + '</text>' : '');
}

function layerWind(hs, x, step, W, H, padT, padB) {
    var sp = hs.map(function (h) { return h.wind || 0; });
    var gu = hs.map(function (h) { return h.gust != null ? h.gust : null; });
    var max = Math.max.apply(null, sp.concat(gu.filter(function (v) { return v != null; })).concat([2]));
    var base = H - padB;
    var y = function (v) { return padT + 14 + (1 - v / max) * (base - padT - 14); };

    var line = sp.map(function (v, i) { return (i ? 'L' : 'M') + x(i) + ' ' + y(v); }).join(' ');
    var gustLine = gu.some(function (v) { return v != null; })
        ? '<path class="wx-gust" d="' + gu.map(function (v, i) { return (i ? 'L' : 'M') + x(i) + ' ' + y(v == null ? sp[i] : v); }).join(' ') + '"/>' : '';

    /* Direction arrows: wind_from_direction is where it BLOWS FROM, so the
       arrow points the way the air is going — from + 180°. Without this the
       wind row was a number with no meaning for anyone rigging a sail, flying
       a drone or deciding which side of the building to open. */
    var arrows = hs.map(function (h, i) {
        var every = hs.length > 14 ? 2 : 1;
        if (i % every || h.dir == null) return '';
        return '<g transform="translate(' + x(i).toFixed(1) + ',' + (base + 16) + ') rotate(' + ((h.dir + 180) % 360).toFixed(0) + ')">' +
               '<path class="wx-arrow" d="M0 -7 L0 7 M0 7 L-3.4 2.4 M0 7 L3.4 2.4"/></g>';
    }).join('');

    var vals = hs.map(function (h, i) {
        var every = hs.length > 14 ? 2 : 1;
        return i % every ? '' : '<text class="wx-tv sm" x="' + x(i).toFixed(1) + '" y="' + (y(sp[i]) - 9).toFixed(1) + '">' + esc(Math.round(wind(sp[i]))) + '</text>';
    }).join('');

    var unit = '<text class="wx-unit" x="' + (rtlNow() ? W - 6 : 6) + '" y="' + (padT + 4) + '" text-anchor="' +
               (rtlNow() ? 'end' : 'start') + '">' + esc(isImperial() ? t('u_mph') : t('u_kmh')) +
               (gu.some(function (v) { return v != null; }) ? ' · ' + esc(t('d_gust')) : '') + '</text>';
    return '<line class="wx-axis" x1="0" y1="' + base + '" x2="' + W + '" y2="' + base + '"/>' +
           gustLine + '<path class="wx-wline" d="' + line + '"/>' + vals + arrows + unit;
}

/* One hour's full reading, opened by tapping the chart. Closing returns to the
   "now" reading rather than blanking the panel. */
function renderHourDetail(idx) {
    var box = $('#wx-hourbox');
    if (!box) return;
    var day = state.days[state.sel];
    if (idx == null || !day) { box.hidden = true; box.innerHTML = ''; state.hour = null; return; }
    var hs = day.hours.filter(function (h) { return h.temp != null; });
    var h = hs[idx];
    if (!h) { box.hidden = true; return; }
    state.hour = idx;

    var bits = [
        [t('d_feels'), tempS(h.feels, 1)],
        [t('d_wind'), h.wind != null ? windS(h.wind) + (h.dir != null ? ' · ' + compass(h.dir) : '') : null],
        [t('d_gust'), h.gust != null ? windS(h.gust) : null],
        [t('d_humidity'), h.hum != null ? Math.round(h.hum) + '%' : null],
        [t('d_pressure'), h.press != null ? pressS(h.press) : null],
        [t('d_cloud'), h.cloud != null ? Math.round(h.cloud) + '%' : null],
        [t('th_precip'), h.rain != null ? rainS(h.rain) : null],
        [t('d_uv'), h.uv != null ? uvS(h.uv) : null]
    ].filter(function (b) { return b[1]; });

    box.hidden = false;
    box.innerHTML =
        '<div class="wx-hourbox-head">' + icon(symIcon(h.sym)) +
            '<strong>' + esc(hourS(h._d)) + '</strong>' +
            '<span>' + esc(tempS(h.temp)) + ' · ' + esc(symLabel(h.sym)) + '</span>' +
            '<button type="button" class="wx-hourbox-x" data-hour-close aria-label="' + esc(t('close')) + '">×</button>' +
        '</div>' +
        '<dl class="wx-hourbox-grid">' + bits.map(function (b) {
            return '<div><dt>' + esc(b[0]) + '</dt><dd>' + esc(b[1]) + '</dd></div>';
        }).join('') + '</dl>';
}

/* ── pressure trend ───────────────────────────────────────────────────────
   Barometric CHANGE is what forecasts weather locally and what people with
   migraines, joint pain, or a boat actually track; a bare "1017 hPa" tells
   nobody anything. Computed over the last six hours of the series we already
   hold, with the conventional 1 hPa/3 h threshold for "changing". */
function pressureTrend() {
    var now = Date.now();
    var pts = state.data.hours.filter(function (h) {
        return h.press != null && h._d && Math.abs(h._d.getTime() - now) < 7 * 3600e3;
    });
    if (pts.length < 4) return null;
    var first = pts[0], last = pts[pts.length - 1];
    var hours = (last._d - first._d) / 3600e3;
    if (hours < 3) return null;
    var per3h = (last.press - first.press) / hours * 3;
    var dir = Math.abs(per3h) < 1 ? 'steady' : (per3h > 0 ? 'rising' : 'falling');
    return { dir: dir, per3h: per3h, now: last.press };
}

function renderDetails() {
    var h = nowHour(), d = state.days[state.sel];
    var tiles = [];
    var add = function (k, ic, label, val, note) {
        if (val == null || val === '—') return;
        tiles.push('<div class="wx-tile" data-k="' + k + '">' +
            '<span class="wx-tile-h">' + ic + esc(label) + '</span>' +
            '<span class="wx-tile-v">' + esc(val) + '</span>' +
            (note ? '<span class="wx-tile-n">' + esc(note) + '</span>' : '') +
        '</div>');
    };
    var g = function (d) { return '<svg class="wx-tico" viewBox="0 0 24 24" aria-hidden="true">' + d + '</svg>'; };

    add('wind', g('<path d="M3 8h11a3 3 0 10-3-3M3 12h15a3 3 0 11-3 3M3 16h9a2.5 2.5 0 11-2.5 2.5"/>'),
        t('d_wind'), h.wind != null ? windS(h.wind) : null, h.dir != null ? compass(h.dir) : null);
    add('gust', g('<path d="M3 9h12a3 3 0 10-3-3M3 15h9a2.5 2.5 0 11-2.5 2.5"/><path d="M17 13l3 3-3 3"/>'),
        t('d_gust'), h.gust != null ? windS(h.gust) : null);
    add('hum', g('<path d="M12 3s6 6.6 6 10.4A6 6 0 016 13.4C6 9.6 12 3 12 3z"/>'),
        t('d_humidity'), h.hum != null ? Math.round(h.hum) + '%' : null);
    add('feels', g('<path d="M14 14.8V5a2 2 0 10-4 0v9.8a4 4 0 104 0z"/>'),
        t('d_feels'), tempS(h.feels, 1), t('feels_note'));

    var pt = pressureTrend();
    add('press', g('<circle cx="12" cy="12" r="9"/><path d="M12 12l4-3"/>'),
        t('d_pressure'), h.press != null ? pressS(h.press) : null,
        pt ? t('press_' + pt.dir) + (pt.dir !== 'steady' ? ' ' + (pt.per3h > 0 ? '+' : '') + pt.per3h.toFixed(1) + ' ' + t('u_hpa') + '/3' + t('u_h') : '') : null);

    add('cloud', g('<path d="M7 18h10a3.6 3.6 0 00.3-7.2 5 5 0 00-9.6-1.2A3.7 3.7 0 007 18z"/>'),
        t('d_cloud'), h.cloud != null ? Math.round(h.cloud) + '%' : null);
    add('dew', g('<path d="M12 3.5s5.5 6 5.5 9.5a5.5 5.5 0 11-11 0C6.5 9.5 12 3.5 12 3.5z"/>'),
        t('d_dew'), h.dew != null ? tempS(h.dew) : null);
    add('uv', g('<circle cx="12" cy="12" r="4"/><path d="M12 2v2.5M12 19.5V22M2 12h2.5M19.5 12H22M4.9 4.9l1.8 1.8M17.3 17.3l1.8 1.8M19.1 4.9l-1.8 1.8M6.7 17.3l-1.8 1.8"/>'),
        t('d_uv'), h.uv != null ? uvS(h.uv) : null, t('uv_clearsky'));
    add('vis', g('<path d="M2 12s3.6-6 10-6 10 6 10 6-3.6 6-10 6-10-6-10-6z"/><circle cx="12" cy="12" r="2.6"/>'),
        t('d_visibility'), h.vis != null ? visS(h.vis) : null);
    if (h.pop != null) add('pop', g('<path d="M12 3.5s5.5 6 5.5 9.5a5.5 5.5 0 11-11 0C6.5 9.5 12 3.5 12 3.5z"/>'),
        t('d_pop'), h.pop + '%', t('pop_provider'));
    if (d && d.mm != null) add('mm', g('<path d="M7 15h10a3.6 3.6 0 00.3-7.2 5 5 0 00-9.6-1.2A3.7 3.7 0 007 15z"/><path d="M9.5 18l-1 3M14.5 18l-1 3"/>'),
        t('d_precip_day'), rainS(d.mm));

    $('#wx-details').innerHTML = tiles.join('');
    renderSun();
}

function compass(deg) {
    var pts = ['n', 'nne', 'ne', 'ene', 'e', 'ese', 'se', 'sse', 's', 'ssw', 'sw', 'wsw', 'w', 'wnw', 'nw', 'nnw'];
    /* MET reports the direction the wind comes FROM, which is the convention
       people expect ("northerly = from the north"). */
    return t('dir_' + pts[Math.round(deg / 22.5) % 16]);
}
function pressS(hpa) { return isImperial() ? (hpa * 0.0295299830714).toFixed(2) + ' ' + t('u_inhg') : Math.round(hpa) + ' ' + t('u_hpa'); }
function visS(m) {
    if (isImperial()) { var mi = m / 1609.34; return (mi >= 10 ? '10+' : mi.toFixed(1)) + ' ' + t('u_mi'); }
    var km = m / 1000; return (km >= 20 ? '20+' : (km < 1 ? Math.round(m) + ' ' + t('u_m') : km.toFixed(1) + ' ' + t('u_km')));
}
function uvS(uv) {
    var v = Math.round(uv);
    var band = v <= 2 ? 'low' : v <= 5 ? 'mod' : v <= 7 ? 'high' : v <= 10 ? 'vhigh' : 'ext';
    return v + ' · ' + t('uv_' + band);
}

/* ── sun & moon ───────────────────────────────────────────────────────────
   Computed locally via GlobeAstro (no extra network call, and it handles polar
   day / polar night, which a naive sunrise formula returns NaN for half the
   year at the latitudes our RU and Nordic readers live at).

   The arc is not decoration: it puts NOW between rise and set, which is the
   difference between reading two timestamps and seeing how much daylight is
   left — the actual question behind "when does it get dark". */
function renderSun() {
    var el = $('#wx-sun');
    if (!el || !window.GlobeAstro || !state.loc) { if (el) el.innerHTML = ''; return; }
    var A = window.GlobeAstro;
    var day = state.days[state.sel] || state.days[0];
    var date = day ? dayAnchor(day) : new Date();

    var rs = A.sunRiseSetLocal(state.loc.lat, date);
    var arc = '', cells = [];

    if (rs.state === 'polar_day' || rs.state === 'polar_night') {
        cells.push('<div class="wx-suncell wide"><dt>' + esc(t('sun')) + '</dt><dd>' +
                   esc(t(rs.state === 'polar_day' ? 'polar_day' : 'polar_night')) + '</dd></div>');
    } else {
        var rise = solarToClock(rs.rise, date), set = solarToClock(rs.set, date);
        var mins = Math.max(0, Math.round((set - rise) / 60000));
        var now = new Date();
        var frac = Math.min(1, Math.max(0, (now - rise) / (set - rise)));
        var isToday = dayKey(now) === day.key;

        var W = 260, H = 92, m = 14, cy = H - 18;
        var px = function (f) { return m + f * (W - 2 * m); };
        var py = function (f) { return cy - Math.sin(f * Math.PI) * (cy - m); };
        var path = [];
        for (var i = 0; i <= 40; i++) { path.push((i ? 'L' : 'M') + px(i / 40).toFixed(1) + ' ' + py(i / 40).toFixed(1)); }
        arc = '<svg class="wx-arc" viewBox="0 0 ' + W + ' ' + H + '" role="img" aria-label="' +
                esc(t('sun_arc_sr', { rise: hourS(rise), set: hourS(set) })) + '">' +
              '<line class="wx-arc-h" x1="0" y1="' + cy + '" x2="' + W + '" y2="' + cy + '"/>' +
              '<path class="wx-arc-p" d="' + path.join(' ') + '"/>' +
              (isToday && frac > 0 && frac < 1
                ? '<path class="wx-arc-done" d="' + path.slice(0, Math.max(2, Math.round(frac * 40)) + 1).join(' ') + '"/>' +
                  '<circle class="wx-arc-sun" cx="' + px(frac).toFixed(1) + '" cy="' + py(frac).toFixed(1) + '" r="6"/>'
                : '') +
              '<text class="wx-arc-t" x="' + m + '" y="' + (H - 3) + '" text-anchor="start">' + esc(hourS(rise)) + '</text>' +
              '<text class="wx-arc-t" x="' + (W - m) + '" y="' + (H - 3) + '" text-anchor="end">' + esc(hourS(set)) + '</text>' +
              '</svg>';

        cells.push('<div class="wx-suncell"><dt>' + esc(t('sunrise')) + '</dt><dd>' + esc(hourS(rise)) + '</dd></div>');
        cells.push('<div class="wx-suncell"><dt>' + esc(t('sunset')) + '</dt><dd>' + esc(hourS(set)) + '</dd></div>');
        cells.push('<div class="wx-suncell"><dt>' + esc(t('daylight')) + '</dt><dd>' +
                   esc(t('hm', { h: Math.floor(mins / 60), m: String(mins % 60).padStart(2, '0') })) + '</dd></div>');
        if (isToday && frac > 0 && frac < 1) {
            var left = Math.max(0, Math.round((set - now) / 60000));
            cells.push('<div class="wx-suncell"><dt>' + esc(t('daylight_left')) + '</dt><dd>' +
                       esc(t('hm', { h: Math.floor(left / 60), m: String(left % 60).padStart(2, '0') })) + '</dd></div>');
        }
    }
    try {
        var mo = A.moon(date);
        var key = String(A.moonPhaseKey(mo.elong)).replace(/^phase_/, '');
        cells.push('<div class="wx-suncell"><dt>' + esc(t('moon')) + '</dt><dd>' + esc(t('moon_' + key)) +
                   '<small>' + Math.round(mo.illum * 100) + '%</small></dd></div>');
    } catch (_) { /* moon is a nicety, never a blocker */ }

    el.innerHTML = arc + '<dl class="wx-sun-grid">' + cells.join('') + '</dl>';
}

/* Where the numbers came from and how old they are. This block is the point
   of the tool as much as the forecast is. */
function renderProvenance() {
    var d = state.data;
    var bits = [];

    var names = d.models.map(function (m) { return t('model_' + m); });
    bits.push('<li>' + esc(t('prov_models', { list: names.join(' + '), n: names.length })) + '</li>');

    if (d.issued) {
        var age = Math.max(0, Math.round((Date.now() - new Date(d.issued).getTime()) / 60000));
        bits.push('<li>' + esc(t('prov_issued', {
            time: fmt(new Date(d.issued), { hour: 'numeric', minute: '2-digit', day: 'numeric', month: 'short' }),
            ago:  age < 60 ? t('min_ago', { n: age }) : t('h_ago', { n: Math.round(age / 60) })
        })) + '</li>');
    }
    if (d.station && d.station.name) {
        bits.push('<li>' + esc(t('prov_station', { name: d.station.name, dist: distS(d.station.km) })) + '</li>');
    }
    /* Season-to-date anomaly. Deliberately worded as a fact about the month,
       not about tomorrow: both terms come from the same reanalysis grid so the
       grid's own bias cancels, which is exactly what a forecast-vs-normal
       comparison could not promise. */
    if (d.climate && d.climate.anomaly != null) {
        var c = d.climate, a = c.anomaly;
        var key = Math.abs(a) < 0.5 ? 'clim_near' : (a > 0 ? 'clim_warmer' : 'clim_colder');
        bits.push('<li class="clim">' + esc(t(key, {
            d: Math.abs(a).toFixed(1), act: tempS(c.actual, 1), norm: tempS(c.normal, 1)
        })) + '</li>');
    }
    if (d.models.length < 2) bits.push('<li class="warn">' + esc(t('prov_one_model')) + '</li>');
    if (d.stale) bits.push('<li class="warn">' + esc(t('prov_stale')) + '</li>');

    $('#wx-prov').innerHTML = '<ul>' + bits.join('') + '</ul>';
}

/* ── data ───────────────────────────────────────────────────────────────── */
function load(loc) {
    if (state.loading) return;
    state.loading = true;
    state.loc = loc;

    $('#wx-place').textContent = loc.name || (loc.lat.toFixed(2) + ', ' + loc.lng.toFixed(2));
    $('#wx-status').hidden = false;
    $('#wx-status').className = 'wx-status loading';
    $('#wx-status').textContent = t('loading');
    $('#wx-err').hidden = true;
    if (window.__stage) window.__stage('fetch');

    var url = CFG.api + '?lat=' + encodeURIComponent(loc.lat.toFixed(4)) + '&lon=' + encodeURIComponent(loc.lng.toFixed(4));
    fetch(url, { credentials: 'omit' })
        .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
        .then(function (j) {
            if (!j || j.err || !j.hours || !j.hours.length) throw new Error(j && j.err ? j.err : 'empty');
            state.data = j;
            state.sel = 0;
            renderAll();
            $('#wx-status').hidden = true;
            remember(loc);
            renderPlaces();
            if (window.__announce) window.__announce(t('sr_loaded', { place: loc.name || '' }));
            if (window.__stage) window.__stage('shown');
            if (window.__aeToolComplete) window.__aeToolComplete({ models: (j.models || []).join('+'), station: !!j.station });
        })
        .catch(function (e) {
            $('#wx-status').hidden = true;
            $('#wx-err').hidden = false;
            $('#wx-err').textContent = t('err_load');
            if (window.__aeDiagnostic) window.__aeDiagnostic({ issue: 'fetch_failed', detail: String(e && e.message || e) });
        })
        .then(function () { state.loading = false; });
}

/* ── saved places ────────────────────────────────────────────────────────
   People check more than one place — home, work, where their parents live,
   where they are flying on Friday. Every competitor keeps a list; we kept one.
   Stored client-side (no account needed) and keyed on rounded coordinates,
   which is also the API's cache granularity, so two picks of the same city
   cannot produce two entries. */
var MAX_PLACES = 8;

function placeId(loc) { return loc.lat.toFixed(2) + ',' + loc.lng.toFixed(2); }

function places() {
    try {
        var v = window.__toolSettings.load('weather', 'places', null);
        return Array.isArray(v) ? v.filter(function (p) { return p && isFinite(p.lat) && isFinite(p.lng); }) : [];
    } catch (_) { return []; }
}

function savePlaces(list) {
    try { window.__toolSettings.save('weather', 'places', list.slice(0, MAX_PLACES)); } catch (_) {}
}

function addPlace(loc) {
    if (!loc.name) return;                       // an unnamed pin is not a "place"
    var list = places().filter(function (p) { return placeId(p) !== placeId(loc); });
    list.unshift({ lat: loc.lat, lng: loc.lng, name: loc.name, tz: loc.tz || '', cc: loc.cc || '' });
    savePlaces(list);
    renderPlaces();
}

function renderPlaces() {
    var el = $('#wx-places');
    if (!el) return;
    var list = places();
    /* One saved place is just "the place you are looking at" — the strip only
       earns its space once there is something to switch between. */
    if (list.length < 2) { el.hidden = true; el.innerHTML = ''; return; }
    var cur = state.loc ? placeId(state.loc) : '';
    el.hidden = false;
    el.innerHTML = list.map(function (p, i) {
        var on = placeId(p) === cur;
        return '<span class="wx-place-chip' + (on ? ' on' : '') + '">' +
            '<button type="button" data-go="' + i + '" aria-current="' + (on ? 'true' : 'false') + '">' + esc(p.name) + '</button>' +
            '<button type="button" class="wx-place-x" data-del="' + i + '" aria-label="' +
                esc(t('place_remove', { name: p.name })) + '">×</button>' +
        '</span>';
    }).join('');
}

function remember(loc) {
    try {
        window.__toolSettings.save('weather', 'loc', {
            lat: loc.lat, lng: loc.lng, name: loc.name || '', tz: loc.tz || '', cc: loc.cc || ''
        });
    } catch (_) {}
    addPlace(loc);
    /* Shareable/bookmarkable URL without a navigation or a scroll jump. */
    try {
        var u = new URL(location.href);
        u.searchParams.set('lat', loc.lat.toFixed(4));
        u.searchParams.set('lon', loc.lng.toFixed(4));
        if (loc.name) u.searchParams.set('place', loc.name); else u.searchParams.delete('place');
        history.replaceState(null, '', u);
    } catch (_) {}
}

/* ── location resolution ────────────────────────────────────────────────── */
function fromUrl() {
    try {
        var p = new URLSearchParams(location.search);
        var la = parseFloat(p.get('lat')), lo = parseFloat(p.get('lon'));
        if (isFinite(la) && isFinite(lo) && Math.abs(la) <= 90 && Math.abs(lo) <= 180) {
            return { lat: la, lng: lo, name: p.get('place') || '', tz: '' };
        }
    } catch (_) {}
    return null;
}

function saved() {
    try {
        var s = window.__toolSettings.load('weather', 'loc', null);
        if (s && isFinite(s.lat) && isFinite(s.lng)) return s;
    } catch (_) {}
    return null;
}

/* The profile city — this is the "already told us where they live" path that
   the cabinet widget will reuse verbatim. */
function fromProfile() {
    var u = window.__USER_LOCATION;
    if (u && isFinite(u.lat) && isFinite(u.lng)) {
        return { lat: u.lat, lng: u.lng, name: u.cityName || '', tz: u.tz || '' };
    }
    return null;
}

/* Fill in a missing IANA zone (URL links and the profile carry none) from the
   cities dataset, so day boundaries are the city's and not the viewer's. */
function fillTz(loc) {
    if (loc.tz) return Promise.resolve(loc);
    if (!window.LocationInput) return Promise.resolve(loc);
    return window.LocationInput.loadData().then(function () {
        var near = window.LocationInput.nearestByCoords(loc.lat, loc.lng);
        if (near) {
            loc.tz = near.tz || '';
            if (!loc.name) loc.name = near.name;
            if (!loc.cc) loc.cc = near.cc;
        }
        return loc;
    }).catch(function () { return loc; });
}

function useGeolocation() {
    if (!navigator.geolocation) { if (window.__geo) __geo.notify('no_api'); return; }
    var btn = $('#wx-geo');
    btn.disabled = true; btn.classList.add('busy');
    navigator.geolocation.getCurrentPosition(function (pos) {
        btn.disabled = false; btn.classList.remove('busy');
        fillTz({ lat: pos.coords.latitude, lng: pos.coords.longitude, name: '', tz: '' }).then(load);
    }, function (err) {
        btn.disabled = false; btn.classList.remove('busy');
        if (window.__geo) __geo.notify(err);
    }, { enableHighAccuracy: false, timeout: 10000, maximumAge: 600000 });
}

/* ── wiring ─────────────────────────────────────────────────────────────── */
function initPicker() {
    var host = $('#wx-picker');
    if (!host || !window.LocationInput) return;
    window.LocationInput.mount(host, {
        tool: 'weather',
        initial: state.loc || undefined,
        t: CFG.li || {},
        defaultTab: 'city',
        onChange: function (c) {
            if (!c || !isFinite(c.lat) || !isFinite(c.lng)) return;
            closePicker();
            load({ lat: c.lat, lng: c.lng, name: c.cityName || c.name || '', tz: c.tz || '', cc: c.cc || '' });
        }
    });
}

function openPicker() {
    var p = $('#wx-picker-wrap');
    p.hidden = false;
    $('#wx-change').setAttribute('aria-expanded', 'true');
    initPicker();
    var inp = p.querySelector('input[type=text],input[type=search]');
    if (inp) inp.focus();
}
function closePicker() {
    $('#wx-picker-wrap').hidden = true;
    $('#wx-change').setAttribute('aria-expanded', 'false');
}

function initUnits() {
    state.units = window.__toolSettings ? window.__toolSettings.units() : 'metric';
    var seg = $('#wx-units');
    if (!seg) return;
    seg.querySelectorAll('button').forEach(function (b) {
        b.setAttribute('aria-pressed', String(b.dataset.u === state.units));
        b.addEventListener('click', function () {
            if (b.dataset.u === state.units) return;
            state.units = b.dataset.u;
            try { window.__toolSettings.setUnits(state.units); } catch (_) {}
            seg.querySelectorAll('button').forEach(function (o) { o.setAttribute('aria-pressed', String(o.dataset.u === state.units)); });
            if (state.data) renderAll();
        });
    });
}

function init() {
    initUnits();
    renderPlaces();

    $('#wx-change').addEventListener('click', function () {
        if ($('#wx-picker-wrap').hidden) openPicker(); else closePicker();
    });
    $('#wx-geo').addEventListener('click', useGeolocation);

    $('#wx-places').addEventListener('click', function (e) {
        var go = e.target.closest('[data-go]');
        if (go) {
            var p = places()[+go.dataset.go];
            if (p && (!state.loc || placeId(p) !== placeId(state.loc))) load(p);
            return;
        }
        var del = e.target.closest('[data-del]');
        if (del) {
            var list = places();
            list.splice(+del.dataset.del, 1);
            savePlaces(list);
            renderPlaces();
        }
    });

    $('#wx-days').addEventListener('click', function (e) {
        var b = e.target.closest('[data-day]');
        if (!b) return;
        state.sel = +b.dataset.day;
        renderDays();
        renderHourly();
        renderBest();
        renderChange();
        renderDetails();
    });

    /* Chart layers — temperature / precipitation / wind on one timeline. */
    $('#wx-layers').addEventListener('click', function (e) {
        var b = e.target.closest('[data-layer]');
        if (!b || b.dataset.layer === state.layer) return;
        state.layer = b.dataset.layer;
        $('#wx-layers').querySelectorAll('[data-layer]').forEach(function (o) {
            o.setAttribute('aria-pressed', String(o.dataset.layer === state.layer));
        });
        renderHourly();
    });

    /* Tap an hour on the chart for its full reading. */
    $('#wx-chart').addEventListener('click', function (e) {
        var hit = e.target.closest('[data-hour]');
        if (!hit) return;
        var i = +hit.dataset.hour;
        renderHourDetail(state.hour === i ? null : i);
    });
    document.addEventListener('click', function (e) {
        if (e.target.closest('[data-hour-close]')) renderHourDetail(null);
    });

    /* The confidence chip explains itself in place — no modal, no navigation. */
    document.addEventListener('click', function (e) {
        var c = e.target.closest('[data-explain]');
        if (!c) return;
        var box = $('#wx-explain');
        var open = box.dataset.open === c.dataset.explain;
        box.hidden = open;
        box.dataset.open = open ? '' : c.dataset.explain;
        if (!open) box.innerHTML = '<p>' + esc(t('conf_explain')) + '</p>';
    });

    var loc = fromUrl() || saved() || fromProfile();
    if (loc) {
        fillTz(loc).then(load);
    } else {
        $('#wx-status').hidden = true;
        openPicker();
        if (window.__stage) window.__stage('need_location');
    }
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();

})();
